<template>
  <div class="risk-template-builder">
    <div class="d-flex align-items-center header">
      <div class="flex-fill header-question">{{ $t("risk.template.question") }}</div>
      <div class="header-type-response d-flex align-items-center justify-content-end">
        <b-button
          size="sm"
          :variant="multiSelect ? 'primary' : 'outline-primary'"
          class="mr-1"
          @click="multiSelect = !multiSelect"
        >
          {{ multiSelect ? $t('custom_forms.selecting_multiple') : $t('custom_forms.select_multiple') }}
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          :disabled="selectedIds.size === 0"
          @click="clearSelection"
        >
          {{ $t('custom_forms.clean') }} ({{ selectedIds.size }})
        </b-button>
      </div>
    </div>

    <!-- Secciones principales -->
    <draggable
      v-model="sections"
      handle=".section-handle"
      :group="{ name: 'sections', pull: true, put: ['sections'] }"
      @update="emitChange"
      class="mb-2"
    >
      <div v-for="(section, si) in sections" :key="section.section_id" class="mb-2">
        <section-header
          :section="section"
          @toggle="section.collapsed = !section.collapsed"
          @remove="removeSection(si)"
          @add-subsection="addSubsection(si)"
        />

        <transition name="fade">
          <div v-if="!section.collapsed" class="mt-2">
            <div class="section-inner">
              <!-- Preguntas en raíz de sección -->
              <draggable
                v-model="section.children"
                handle=".handle"
                :group="{ name: 'fields', pull: true, put: ['fields'] }"
                @change="emitChange"
                class="section-box"
              >
                <template-field
                  v-for="(item, index) in section.children"
                  :key="item.item_id"
                  :data="item"
                  :selected="itemSelected && itemSelected.item_id === item.item_id"
                  :section-fields="linearFieldsOfSection(section)"
                  :index-in-section="linearIndexOf(section, item)"
                  :select-mode="multiSelect"
                  :is-selected="selectedIds.has(item.item_id)"
                  @toggle-select="toggleItemSelection(item)"
                  @item-selected="onItemSelected"
                  @show-modal="showModal"
                  @remove="removeFieldFromSection(si, index)"
                />
              </draggable>

              <!-- Hint vacío -->
              <div
                v-if="(!section.children || section.children.length === 0) && (!section.subsections || section.subsections.length === 0)"
                class="text-muted small pl-1"
              >
                {{ $t("risk.template.drop_or_add_here") }}
              </div>

              <!-- Subsecciones -->
              <draggable
                v-model="section.subsections"
                handle=".subsection-handle"
                :group="{ name: 'subsections', pull: true, put: ['subsections'] }"
                @change="emitChange"
                class="mt-2"
              >
                <div
                  v-for="(sub, ssi) in section.subsections"
                  :key="sub.id"
                  class="mb-2"
                >
                  <subsection-header
                    :sub="sub"
                    :multi-select="multiSelect"
                    @toggle="sub.collapsed = !sub.collapsed"
                    @remove="removeSubsection(si, ssi)"
                    @add-field="addFieldToSubsection(si, ssi)"
                    @move-selected-here="moveSelectedToSubsection(si, ssi)"
                  />

                  <transition name="fade">
                    <div v-if="!sub.collapsed" class="mt-1 subsection-inner">
                      <draggable
                        v-model="sub.children"
                        handle=".handle"
                        :group="{ name: 'fields', pull: true, put: ['fields'] }"
                        @change="emitChange"
                        class="section-box"
                      >
                        <template-field
                          v-for="(item, index) in sub.children"
                          :key="item.item_id"
                          :data="item"
                          :selected="itemSelected && itemSelected.item_id === item.item_id"
                          :section-fields="linearFieldsOfSection(section)"
                          :index-in-section="linearIndexOf(section, item)"
                          :select-mode="multiSelect"
                          :is-selected="selectedIds.has(item.item_id)"
                          @toggle-select="toggleItemSelection(item)"
                          @item-selected="onItemSelected"
                          @show-modal="showModal"
                          @remove="removeFieldFromSubsection(si, ssi, index)"
                        />
                      </draggable>
                      <div v-if="!sub.children || sub.children.length === 0" class="text-muted small pl-1">
                        {{ $t("risk.template.drop_or_add_here") }}
                      </div>
                    </div>
                  </transition>
                </div>
              </draggable>

            </div>
          </div>
        </transition>

        <!-- Separador de sección -->
        <div v-if="si < sections.length - 1" class="section-separator"></div>
      </div>
    </draggable>

    <!-- Sin sección -->
    <div class="unsectioned mb-3">
      <div class="d-flex align-items-center mb-1">
        <strong>{{ $t("risk.template.no_section") }}</strong>
      </div>

      <draggable
        v-model="unsectioned"
        handle=".handle"
        :group="{ name: 'fields', pull: true, put: ['fields'] }"
        @change="emitChange"
        class="section-box"
      >
        <template-field
          v-for="(item, index) in unsectioned"
          :key="item.item_id"
          :data="item"
          :selected="itemSelected && itemSelected.item_id === item.item_id"
          :section-fields="unsectioned"
          :index-in-section="index"
          :select-mode="multiSelect"
          :is-selected="selectedIds.has(item.item_id)"
          @toggle-select="toggleItemSelection(item)"
          @item-selected="onItemSelected"
          @show-modal="showModal"
          @remove="removeFieldFromUnsectioned(index)"
        />
      </draggable>
    </div>

    <!-- Modal tipos -->
    <template-field-types-modal
      :key="itemSelected && itemSelected.item_id"
      :type="itemSelected ? itemSelected.type : ''"
      :field-type-cfg="itemSelected ? itemSelected.options.fieldTypeCfg : { data: [] }"
      @update:type="onUpdateFieldType"
      @update:fieldTypeCfg="onUpdateFieldTypeCfg"
      @modal-close="onModalClose"
    />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { v4 as uuid } from "uuid";
import { BButton } from "bootstrap-vue";

import TemplateField from "./TemplateField.vue";
import TemplateFieldTypesModal from "./TemplateFieldTypesModal.vue";
import SectionHeader from "./SectionHeader.vue";
import SubsectionHeader from "./SubsectionHeader.vue";
import { fieldStructure } from "./config";

export default {
  name: "SecuritySelfAuditTemplateBuilder",
  props: {
    items: { type: Array, default: () => [] },
    isValid: Boolean,
  },
  components: {
    draggable,
    TemplateField,
    TemplateFieldTypesModal,
    SectionHeader,
    SubsectionHeader,
    BButton,
  },
  data() {
    return {
      sections: [],
      unsectioned: [],
      itemSelected: null,
      _isBuildingFromProps: false,
      _lastFlatSignature: "",
      // selección múltiple
      multiSelect: false,
      selectedIds: new Set(),
    };
  },
  mounted() {
    this.buildFromItems(this.items);
  },
  watch: {
    items: {
      deep: true,
      handler(val) {
        const incomingSig = this.signatureOf(val);
        if (incomingSig === this._lastFlatSignature) {
          this.validateFields();
          return;
        }
        this.buildFromItems(val);
      },
    },
  },
  methods: {
    //Public API to call from TemplateConfig by ref
    addSection() {
      this.sections.push({
        section_id: uuid(),
        name: this.$t("risk.template.new_section"),
        collapsed: false,
        progressive: true,
        children: [],
        subsections: [],
      });
      this.emitChange();
    },

    addSubsection(si) {
      const section = this.sections[si];
      if (!section.subsections) this.$set(section, "subsections", []);
      section.subsections.push({
        id: uuid(),
        name: this.$t("risk.template.new_section"),
        collapsed: false,
        children: [],
      });
      this.emitChange();
    },

    //Serialization
    flatten() {
      const out = [];
      this.sections.forEach((sec) => {
        (sec.children || []).forEach((child) => {
          out.push({
            ...child,
            section: sec.name || null,
            subsection: null,
            section_progressive: !!sec.progressive,
          });
        });
        (sec.subsections || []).forEach((sub) => {
          (sub.children || []).forEach((child) => {
            out.push({
              ...child,
              section: sec.name || null,
              subsection: sub.name || null,
              section_progressive: !!sec.progressive,
            });
          });
        });
      });
      this.unsectioned.forEach((child) =>
        out.push({ ...child, section: null, subsection: null })
      );
      return out;
    },

    buildFromItems(items = []) {
      this._isBuildingFromProps = true;
      try {
        const bySection = new Map();
        const order = [];
        const unsec = [];

        (items || []).forEach((it) => {
          const sec = it.section || null;
          const sub = it.subsection || null;
          const f = { ...it };
          delete f.section;
          delete f.subsection;

          if (sec) {
            if (!bySection.has(sec)) {
              bySection.set(sec, { root: [], subs: new Map(), orderSub: [] });
              order.push(sec);
            }
            const bucket = bySection.get(sec);
            if (sub) {
              if (!bucket.subs.has(sub)) {
                bucket.subs.set(sub, []);
                bucket.orderSub.push(sub);
              }
              bucket.subs.get(sub).push(f);
            } else {
              bucket.root.push(f);
            }
          } else {
            unsec.push(f);
          }
        });

        if (bySection.size > 0) {
          const existingByName = new Map(
            (this.sections || []).map((s) => [s.name || "", s])
          );

          this.sections = order.map((secName) => {
            const prev = existingByName.get(secName);
            const bucket = bySection.get(secName);

            const prevSubsByName = new Map(
              ((prev && prev.subsections) || []).map((s) => [s.name || "", s])
            );

            const subsections = (bucket.orderSub || []).map((subName) => {
              const prevSub = prevSubsByName.get(subName);
              const fields = bucket.subs.get(subName) || [];
              return {
                id: prevSub ? prevSub.id : uuid(),
                name: subName,
                collapsed: prevSub ? !!prevSub.collapsed : false,
                children: fields.map((f) => ({ ...f })),
              };
            });

            const fromChild = (bucket.root || []).find(
              (f) => typeof f.section_progressive !== "undefined"
            );

            return {
              section_id: prev ? prev.section_id : uuid(),
              name: secName,
              collapsed: prev ? !!prev.collapsed : false,
              progressive: fromChild
                ? !!fromChild.section_progressive
                : prev
                ? !!prev.progressive
                : true,
              children: (bucket.root || []).map((f) => {
                const clone = { ...f };
                delete clone.section_progressive;
                return clone;
              }),
              subsections: subsections || [],
            };
          });

          const idsInSections = new Set();
          this.sections.forEach((s) => {
            (s.children || []).forEach((f) => idsInSections.add(f.item_id));
            (s.subsections || []).forEach((sub) =>
              (sub.children || []).forEach((f) => idsInSections.add(f.item_id))
            );
          });
          this.unsectioned = (items || [])
            .filter((i) => !idsInSections.has(i.item_id))
            .map((i) => ({ ...i, section: undefined, subsection: undefined }));
        } else {
          this.sections = (this.sections || []).map(s => ({ ...s, subsections: s.subsections || [] }));
          this.unsectioned = unsec;
        }

        this._lastFlatSignature = this.signatureOf(items || []);
        this.validateFields();
      } finally {
        this._isBuildingFromProps = false;
      }
    },

    signatureOf(items) {
      try {
        return JSON.stringify(
          (items || []).map((i) => ({
            id: i.item_id,
            sec: i.section || null,
            sub: i.subsection || null,
            nm: i.name || "",
            ty: i.type || "",
            optlen:
              ((i.options &&
                i.options.fieldTypeCfg &&
                i.options.fieldTypeCfg.data) ||
                []).length,
          }))
        );
      } catch (e) {
        return "";
      }
    },
    //Multiple Selection
    toggleItemSelection(item) {
      const id = item && item.item_id;
      if (!id) return;
      if (this.selectedIds.has(id)) this.selectedIds.delete(id);
      else this.selectedIds.add(id);
      this.selectedIds = new Set(this.selectedIds);
    },
    clearSelection() {
      this.selectedIds = new Set();
    },
    //Move selected to subsection
    moveSelectedToSubsection(si, ssi) {
      if (this.selectedIds.size === 0) return;

      const targetSection = this.sections[si];
      const targetSub = targetSection.subsections[ssi];

      const moved = [];
      const extractFrom = (arr) => {
        if (!Array.isArray(arr)) return [];
        const keep = [];
        for (const f of arr) {
          if (f && this.selectedIds.has(f.item_id)) moved.push(f);
          else keep.push(f);
        }
        return keep;
      };
      //delete for everywhere
      this.sections.forEach(sec => {
        sec.children = extractFrom(sec.children);
        (sec.subsections || []).forEach(sub => {
          sub.children = extractFrom(sub.children);
        });
      });
      this.unsectioned = extractFrom(this.unsectioned);

      //avoid duplicated
      const existing = new Set((targetSub.children || []).map(f => f.item_id));
      const toAdd = moved.filter(f => !existing.has(f.item_id));
      targetSub.children = (targetSub.children || []).concat(toAdd);

      this.emitChange();
      this.clearSelection();
    },
    //Mutators
    addFieldToSubsection(si, ssi) {
      const f = this.newField();
      this.sections[si].subsections[ssi].children.push(f);
      this.itemSelected = f;
      this.emitChange();
    },

    removeSection(si) {
      if (!confirm(this.$t("risk.template.confirm_delete_section"))) return;
      const section = this.sections[si];
      this.unsectioned.push(...(section.children || []));
      (section.subsections || []).forEach((sub) =>
        this.unsectioned.push(...(sub.children || []))
      );
      this.sections.splice(si, 1);
      this.emitChange();
    },
    removeSubsection(si, ssi) {
      if (!confirm(this.$t("risk.template.confirm_delete_section"))) return;
      const sub = this.sections[si].subsections[ssi];
      this.unsectioned.push(...(sub.children || []));
      this.sections[si].subsections.splice(ssi, 1);
      this.emitChange();
    },
    removeFieldFromSection(si, idx) {
      if (!confirm(this.$t("risk.template.confirm_delete_field"))) return;
      const f = this.sections[si].children[idx];
      if (f && this.selectedIds.has(f.item_id)) this.selectedIds.delete(f.item_id);
      this.sections[si].children.splice(idx, 1);
      this.emitChange();
    },
    removeFieldFromSubsection(si, ssi, idx) {
      if (!confirm(this.$t("risk.template.confirm_delete_field"))) return;
      const f = this.sections[si].subsections[ssi].children[idx];
      if (f && this.selectedIds.has(f.item_id)) this.selectedIds.delete(f.item_id);
      this.sections[si].subsections[ssi].children.splice(idx, 1);
      this.emitChange();
    },
    removeFieldFromUnsectioned(idx) {
      if (!confirm(this.$t("risk.template.confirm_delete_field"))) return;
      const f = this.unsectioned[idx];
      if (f && this.selectedIds.has(f.item_id)) this.selectedIds.delete(f.item_id);
      this.unsectioned.splice(idx, 1);
      this.emitChange();
    },
    newField() {
      const f = JSON.parse(JSON.stringify(fieldStructure));
      f.item_id = uuid();
      return f;
    },
    //Utilities
    linearFieldsOfSection(section) {
      const arr = [];
      (section.children || []).forEach(f => arr.push(f));
      (section.subsections || []).forEach(sub => (sub.children || []).forEach(f => arr.push(f)));
      return arr;
    },
    linearIndexOf(section, item) {
      const arr = this.linearFieldsOfSection(section);
      return Math.max(0, arr.findIndex(f => f.item_id === item.item_id));
    },
    absorbRootIntoSubsection(si, ssi) {
      const section = this.sections[si];
      const sub = section.subsections[ssi];
      const root = section.children || [];
      if (!root.length) return;
      sub.children = (sub.children || []).concat(root);
      section.children = [];
      this.emitChange();
    },
    elevateSubsectionToRoot(si, ssi) {
      const section = this.sections[si];
      const sub = section.subsections[ssi];
      const items = sub.children || [];
      if (!items.length) return;
      section.children = (section.children || []).concat(items);
      sub.children = [];
      this.emitChange();
    },

    // Sync con padre
    emitChange() {
      if (this._isBuildingFromProps) return;
      const flat = this.flatten();
      this._lastFlatSignature = this.signatureOf(flat);
      this.$emit("update:items", flat);
      this.validateFields();
    },

    // Modal
    onUpdateFieldType(val) {
      if (!this.itemSelected) return;
      this.itemSelected.type = val;
      this.emitChange();
    },
    onUpdateFieldTypeCfg(cfg) {
      if (!this.itemSelected) return;
      if (!this.itemSelected.options) this.$set(this.itemSelected, "options", {});
      this.$set(this.itemSelected.options, "fieldTypeCfg", cfg || { data: [] });
      this.emitChange();
    },
    onItemSelected(item) {
      this.itemSelected = item;
    },
    showModal(item) {
      this.itemSelected = item;
      this.$nextTick(() =>
        this.$root.$emit("bv::show::modal", "template_field_types_modal")
      );
    },
    onModalClose() {
      this.itemSelected = null;
    },
    //Validations
    validateFields() {
      let invalid = false;

      (this.sections || []).forEach((sec) => {
        if (!sec.name || !sec.name.trim()) invalid = true;

        const linear = this.linearFieldsOfSection(sec);
        const idToIndex = new Map(linear.map((f, idx) => [f.item_id, idx]));

        const validateOne = (field, idx) => {
          field.errors = field.errors || {};
          field.hasErrors = !!field.hasErrors;
          field.errors.missingName = !field.name || field.name === "";
          field.errors.missingType = !field.type || field.type === "";

          let invalidConditions = false;
          let invalidOrder = false;
          if (Array.isArray(field.show_if)) {
            field.show_if.forEach((c) => {
              if (!c || !c.field) return;
              const refIdx = idToIndex.has(c.field) ? idToIndex.get(c.field) : -1;
              if (refIdx === -1) invalidConditions = true;
              else if (refIdx >= idx) invalidOrder = true;
            });
          }
          field.errors.invalidConditions = invalidConditions;
          field.errors.invalidOrder = invalidOrder;

          field.errors.hasErrors =
            field.errors.missingName ||
            field.errors.missingType ||
            field.errors.invalidConditions ||
            field.errors.invalidOrder;

          if (field.errors.hasErrors) invalid = true;
        };

        linear.forEach((f, idx) => validateOne(f, idx));
      });

      (this.unsectioned || []).forEach((field) => {
        field.errors = field.errors || {};
        field.hasErrors = !!field.hasErrors;
        field.errors.missingName = !field.name || field.name === "";
        field.errors.missingType = !field.type || field.type === "";
        field.errors.hasErrors = field.errors.missingName || field.errors.missingType;
        if (field.errors.hasErrors) invalid = true;
      });

      this.$emit("update:isValid", !invalid);
    },
  },
};
</script>

<style lang="scss" scoped>
.risk-template-builder {
  border: 1px solid #d1d1d1;
  .header {
    height: 40px;
    padding: 0 10px;
    background-color: #f4f4f4;
    border-bottom: 1px solid #d1d1d1;
    &-type-response {
      padding-left: 10px;
      width: calc(310px - 1px);
      height: 100%;
      line-height: 33px;
      border-left: 1px solid #d1d1d1;
    }
  }
}

.section-inner { padding-left: 16px; }
.subsection-inner { padding-left: 12px; }

.section-box { border: 1px dashed #ddd; padding: 0.5rem; border-radius: 6px; background: #fff; }

.section-separator { border-top: 1px solid #e6e6e6; margin: 12px 0 16px; }
.unsectioned { border-top: 1px solid #eee; padding-top: 0.75rem; }

.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
